import React, {useEffect} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import SVG from 'react-inlinesvg';
import moment from 'moment';
import {personalInfoKeys, mainInfo} from '../UserDetailsUIHelpers';
import {InfoItem} from './InfoItem';
import ImageOkOrNot from './ImageOkOrNot';
import Empty from './Empty';
import PopupUpdateUserStatus from './PopupUpdateUserStatus';
import InvitedBy from './InvitedBy';
import SelectHistoryPoint from './SelectHistoryPoint';
import * as actionsPartners from '../../Partners/_redux/partnersActions';
import {toAbsoluteUrl} from '../../../../_metronic/_helpers';
import styles from '../styles.module.scss';
import classnames from 'classnames';

const defaultUserPic = toAbsoluteUrl('/media/atzmai/PEO-smiley_smile.svg');

export const PersonalInfo = ({data}) => {
  const {
    pic,
    firstName,
    lastName,
    agentId
  } = data;
  const userIsDisabled = data?.disabled ? data?.disabled : false;
  const prefix = 'userDetails-personalInfo';
  const intl = useIntl();
  const notThroughPartner = intl.formatMessage({id: `${prefix}-affiliateCompanyID-none`, defaultMessage: 'Not through a partner',});
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {dispatch(actionsPartners.getPartnersOptions());}, []);

  const affiliateCompanyOptions = useSelector((state) => state.partners.partnersOptions, shallowEqual);

  const goToInvoicesWithAgent = () => history.push(`/invoices/?agentID=${agentId}`);
  const goToReceiptsWithAgent = () => history.push(`/receipts/?agentID=${agentId}`);
  const goToTransfersByAgent = () => history.push(`/transfersByAgent/${agentId}`);

  const mapInfoItems = item => {
    const {id, key, options} = item;
    const translationKey = `userDetails-personalInfo-${key}`;
    let value = data[key];
    const valueIsExist = !(value === null
      || value === undefined
      || typeof value === 'boolean'
      || value.length === 0);
    let props = {
      valueCustomClassName: (valueIsExist)
        ? 'label label-lg label-light-primary label-inline'
        : '',
      keyName: key,
      entireValue: null,
      ...options,
    };

    if (key === 'liveOutsideIsrael' ||
      key === 'otherIncomeExists' ||
      key === 'approveAdvertisement' ||
      key === 'approvePension' ||
      key === 'approveInsurance'
    ) {
      value = (
        <ImageOkOrNot
          checked={value}
          className={classnames('boolean-img')}
        />
      );
      props = {...props, containerCustomClassName: 'position-relative'};
    }
    if (!valueIsExist &&
      key !== 'liveOutsideIsrael' &&
      key !== 'otherIncomeExists' &&
      key !== 'approveAdvertisement' &&
      key !== 'approvePension' &&
      key !== 'approveInsurance'
    ) value = '';

    if ((key === 'birthDate')
      || (key === 'createdAt')
      || (key === 'dateOfAliyah')
      || (key === 'notificationsTimestamp')
      || (key === 'freelanceStartDate')) {
      value = (value) ? moment(value).format('DD/MM/YYYY') : '';
    }

    if (key === 'street' || key === 'houseNum' || key === 'appartmentNum') {
      const {street, houseNum, appartmentNum} = data;
      props = {...props, address: {street, houseNum, appartmentNum}};
    }

    if (key === 'receiveNotifications') {
      const receiveNotificationsValue = (data[key] === false)
        ? data[key]
        : true;
      value = (
        <ImageOkOrNot
          checked={receiveNotificationsValue}
        />
      );
      props = {
        ...props,
        entireValue: true,
        valueCustomClassName: '',
      };
    }

    if (key === 'affiliateCompanyID') {
      const filteredDDValue = affiliateCompanyOptions.filter(item => item.value === data[key]);
      value = filteredDDValue.length > 0
        ? filteredDDValue[0].label
        : notThroughPartner;
      props = {
        ...props,
        entireValue: true,
        valueCustomClassName: 'label label-lg label-light-primary label-inline'
      };
    }

    if (!value && (key === 'cameFrom' || key === 'cameFromText')) {
      return null;
    }

    props = {...props, id, value, translationKey};

    return <InfoItem {...props} alignType="flex"/>;
  };

  const renderPersonalInfo = () => {

    return (
      <div className="navi-item">
        <div className={'px-2 mb-4'}>
          {mainInfo.map(mapInfoItems)}
        </div>
        <div className="navi-link py-4 active">
          <span className="navi-icon mr-2">
            <span className="svg-icon">
              <SVG src={toAbsoluteUrl('/media/svg/Layers.svg')}/>
              {' '}
            </span>
          </span>
          <span className="font-size-h5 font-weight-bolder text-dark">פרטים כלליים</span>
        </div>
        <div className="px-2">
          {personalInfoKeys.map(mapInfoItems)}
        </div>
      </div>
    );
  };

  return (
    <div className={classnames('card-body', styles.personalInfo)}>
      {userIsDisabled &&
        <div className={styles.disabledContainer}>
          <FormattedMessage id={`${prefix}-isDisabled`} defaultMessage="Disabled"/>
        </div>
      }
      <div className={styles.personalDataContainer}>
        <div className={'symbol symbol-60 symbol-xxl-100 mr-5 align-self-start align-self-xxl-center'}>
          <div
            className="symbol-label"
            style={{backgroundImage: `url(${pic || defaultUserPic})`}}
          />
        </div>
        <div className={'w-100'}>
          <div className="w-100 d-flex justify-content-between">
            <div className={'font-weight-bolder font-size-h5 text-dark-75 text-hover-primary'}>
              {firstName} {lastName}
            </div>
            <SelectHistoryPoint/>
          </div>
          <div className="text-muted">{agentId}</div>
          <div className={styles.buttonsContainer}>
            <button
              onClick={goToInvoicesWithAgent}
              disabled={!agentId || userIsDisabled}
              className={classnames(styles.button, `btn btn-sm btn-primary`)}
            >
              <FormattedMessage id={`${prefix}-btn-invoices`} defaultMessage="Invoices"/>
            </button>
            <button
              onClick={goToReceiptsWithAgent}
              disabled={!agentId || userIsDisabled}
              className={classnames(styles.button, `btn btn-sm btn-primary`)}
            >
              <FormattedMessage id={`${prefix}-btn-receipts`} defaultMessage="Receipts"/>
            </button>
            <button
              onClick={goToTransfersByAgent}
              disabled={!agentId || userIsDisabled}
              className={classnames(styles.button, `btn btn-sm btn-primary`)}
            >
              <FormattedMessage id={`${prefix}-btn-transfers`} defaultMessage="Transfers"/>
            </button>
          </div>
        </div>
      </div>
      <div className="py-4 navi">
        {
          (Object.keys({...data}).length === 0)
            ? <Empty/>
            : renderPersonalInfo()
        }
      </div>
      <InvitedBy data={data}/>
      <PopupUpdateUserStatus data={data}/>
    </div>
  );
};

PersonalInfo.propTypes = {};
